/* ════════════════════════════════════════════════════════════════════════ */
/* #region(collapsed) Developer Documentation */
/*
  @license
  • COPYRIGHT NOTICE:
    © CROUIN 2021. All rights reserved. Powered by MaxDesign.
    ⚠ FR : CONTENU PROTÉGÉ PAR LES DROITS D'AUTEURS (article L. 111-1 du code de la propriété intellectuelle). Cette ressource ne peut pas être utilisée, diffusée ou exploitée sans l'approbation de ses auteurs. Pour plus d'informations, veuillez contacter admin(at)maxdesign.expert
    ⚠ EN : CONTENT PROTECTED WITH AUTHOR COPYRIGHTS (section L. 111-1 of the intellectual property code). This resource may not be used, distributed or exploited without the approval of its authors. For any information request, please contact admin(at)maxdesign.expert
 
  • INTRODUCTION:
    Related to ./sections/media-text-maxdesign.liquid
    This module features mostly rely on HTML custom elements: media-text-maxesign, video-maxdesign.

  • MORE INFORMATION:
    - Contact us at admin(at)maxdesign.expert
    - Visit shopify cart API https://shopify.dev/api/ajax/reference/cart
*/
/* #endregion ═════════════════════════════════════════════════════════════ */

class MediaText extends HTMLElement {
  connectedCallback() {
    this.settings = this.json(this);
    if (this.settings.slider.enabled) this.initSlider();
    if (this.settings.animations.wheel || this.settings.animations.number) this.initAnimations();
  }

  /* ════════════════════════════════════════════════════════════════════════ */
  /* #region(collapsed) Slider Methods */

  initSlider() {
    this.medias = this.querySelectorAll('.mtmd__media-item');
    this.viewport = this.querySelector('.mtmd__media');
    this.options = { loop: true, slidesToScroll: 1, containScroll: 'trimSnaps', inViewThreshold: 1 };
    this.plugins = [EmblaCarouselClassNames({ snapped: '' })];
    this.slider = EmblaCarousel(this.viewport, this.options, this.plugins);
    new ResizeObserver(this.setMediaFlexBasis.bind(this)).observe(this);
    if (this.settings.video.enabled) this.slider.on('select', this.pauseVideos.bind(this));
  }

  /**
   * Scale media to fit section height
   * Do not scale up if the scrollWidth is too small
   */
  setMediaFlexBasis() {
    if (this.mobile) return;
    this.style.removeProperty('--media-flex-desktop');
    const { height: heightTarget } = this.getBoundingClientRect();
    const { width: mediaWidth, height: mediaHeight } = this.medias[0].getBoundingClientRect();
    const flexGap = 20;
    const flexBasis = Math.floor((heightTarget * mediaWidth) / mediaHeight + flexGap);
    const totalWidth = this.slider.slideNodes().length * flexBasis;
    const fitHeight = totalWidth - this.viewport.offsetWidth > flexBasis / 3;
    if (fitHeight) this.style.setProperty('--media-flex-desktop', `${flexBasis}px`);
  }

  /**
   * Pause all videos when the selected slide changes
   */
  pauseVideos() {
    const playingVideos = this.querySelectorAll('.video-md[data-playing="true"]');
    playingVideos.forEach(video => (video.play = false));
  }
  /* #endregion ═════════════════════════════════════════════════════════════ */

  /* ════════════════════════════════════════════════════════════════════════ */
  /* #region(collapsed) Animations methods */
  initAnimations() {
    this.triggers = this.querySelectorAll('.mtmd__number');
    this.intersectionObserver = new IntersectionObserver(this.progressAnimate.bind(this), { rootMargin: '10px' });
    this.triggers.forEach(trigger => {
      const value = trigger.firstElementChild.textContent.match(/\d+(?:[,. ]\d+)*/);
      trigger.dataset.value = value;
      trigger.style.setProperty('--value', value);
      this.intersectionObserver.observe(trigger);
    });
  }

  /**
   * Trigger progress animation
   * @param {Object} entries - block intersecting entries
   */
  progressAnimate(entries) {
    entries.forEach(entry => {
      if (entry.isIntersecting) {
        entry.target.dataset.intersected = true;
        if (entry.target.hasAttribute('data-number-animate')) this.textAnimate(entry.target);
        this.intersectionObserver.unobserve(entry.target);
      }
    });
  }

  textAnimate(target) {
    const node = target.firstElementChild;
    const text = node.innerHTML;
    const endValue = target.dataset.value;
    if (isNaN(endValue)) return;
    var startValue = 0;
    const duration = Math.floor(1300 / endValue);
    const counter = setInterval(() => {
      startValue += 1;
      node.innerHTML = text.replace(/\d+(?:[,. ]\d+)*/, startValue);
      if (startValue == endValue) clearInterval(counter);
    }, duration);
  }
  /* #endregion ═════════════════════════════════════════════════════════════ */

  /* ════════════════════════════════════════════════════════════════════════ */
  /* #region(collapsed) Getters & Setters */

  /**
   * @returns {Object} custom element settings
   */
  get json() {
    return scope => JSON.parse(scope.querySelector(':scope > [type="application/json"]')?.innerText || '{}');
  }

  get mobile() {
    return matchMedia('(max-width:1024px)').matches;
  }
  /* #endregion ═════════════════════════════════════════════════════════════ */
}

/***********************************************************************************************************/
/***********************************************************************************************************/

class Video extends HTMLElement {
  connectedCallback() {
    this.video = this.querySelector('video');
    this.player = this.querySelector('button[data-player-btn]');
    this.muter = this.querySelector('button[data-muter-btn]');
    this.video.addEventListener('play', this.onVideoPlaying.bind(this));
    this.video.addEventListener('pause', this.onVideoPausing.bind(this));
    this.player.addEventListener('click', this.onPlayerToggle.bind(this));
    this.muter.addEventListener('click', this.onMuterToggle.bind(this));
    this.intersectionObserver = new IntersectionObserver(this.onLazyload.bind(this), { rootMargin: '1250px' });
    this.intersectionObserver.observe(this.video);
  }

  /* ════════════════════════════════════════════════════════════════════════ */
  /* #region(collapsed) User Events */
  onLazyload([entry]) {
    if (!entry.isIntersecting) return;
    const sources = this.video.querySelectorAll('source');
    sources.forEach(source => source.hasAttribute('data-src') && (source.src = source.dataset.src));
    this.video.load();
    this.dataset.loaded = 'true';
    this.play = this.video.autoplay;
    this.intersectionObserver.unobserve(this.video);
  }

  onVideoPlaying() {
    this.setAttribute('data-playing', true);
  }

  onVideoPausing() {
    this.setAttribute('data-playing', false);
  }

  onPlayerToggle() {
    if (!this.playing) {
      this.mute = false;
      this.play = true;
    } else {
      this.play = false;
    }
  }

  onMuterToggle() {
    this.mute = !this.muted;
    this.play = true;
  }
  /* #endregion ═════════════════════════════════════════════════════════════ */

  /* ════════════════════════════════════════════════════════════════════════ */
  /* #region(collapsed) Auxiliary Methods */
  fallbackPlayer() {
    this.setAttribute('data-playing', false);
    this.video.addEventListener(
      'click',
      () => {
        this.mute = false;
        this.play = true;
      },
      { once: true }
    );
  }
  /* #endregion ═════════════════════════════════════════════════════════════ */

  /* ════════════════════════════════════════════════════════════════════════ */
  /* #region(collapsed) Getters & Setters */
  get playing() {
    return !!(this.video.currentTime > 0 && !this.video.paused && !this.video.ended && this.video.readyState > 2);
  }

  get muted() {
    return this.video.hasAttribute('muted');
  }

  set play(play) {
    if (play) this.video.play().catch(this.fallbackPlayer.bind(this));
    else this.video.pause();
  }

  set mute(mute) {
    this.video.toggleAttribute('muted', mute);
    this.video.muted = mute;
  }
  /* #endregion ═════════════════════════════════════════════════════════════ */
}

customElements.define('video-maxdesign', Video);
customElements.define('media-text-maxdesign', MediaText);
